/*
 * File: FollowerChart.tsx
 * Project: a-a
 * File Created: Tuesday, 24th June 2025 6:23:44 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Tuesday, 4th November 2025 11:11:59 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2025 Mutual Mobile, Mutual Mobile
 */
import { Box, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';
import zeplinColor from '../../../../../theme/Color';
import { PieChart } from '@mui/x-charts';

// Utility function to format numbers
const formatNumber = (num: number): string => {
  if (num >= 1_000_000_000) {
    return `${(num / 1_000_000_000).toFixed(2)}B`; // Billion
  } else if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(2)}M`; // Million
  } else if (num >= 1_000) {
    return `${(num / 1_000).toFixed(2)}k`; // Thousand
  }
  return num.toString(); // Less than 1,000
};

const FollowerChart = ({ data, loading }: { data: any; loading: boolean }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm')); // < 600px

  const isXl = useMediaQuery(theme.breakpoints.up('xl')); // 1200px - 1536px
  const platformColors = [
    zeplinColor.Blue4, // Instagram
    '#F3A03F', // Facebook
    '#8F42EB', // Snapchat
    '#45C7EB', // TikTok
    '#DD67F0', // X
    '#2BDF89', // YouTube
  ];
  return (
    <Box
      sx={{
        borderRadius: '14px',
        padding: { xs: '12px 16px', xl: '16px' },
        border: `1px solid ${zeplinColor.Neutral5}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        // height: '338px',
        overflowY: 'auto',
        position: 'relative',
      }}
    >
      {' '}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          variant='responsiveHeading3'
          sx={{
            position: 'sticky',
            top: 0,
            width: '100%',
            backgroundColor: zeplinColor.Neutral1,
            zIndex: 1,
          }}
        >
          Followers
        </Typography>
        {/* <Typography
          variant='heading3'
          sx={{
            position: 'sticky',
            top: 0,
            width: '100%',
            backgroundColor: zeplinColor.Neutral1,
            zIndex: 1,
          }}
        >
          Followers
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: '4px',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'flex-end',
          }}
        >
          <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <Typography variant='label1' color={zeplinColor.Neutral8}>
              New Monthly
            </Typography>
            <Typography variant='heading4'>{data?.formattedNewMonthly}</Typography>
          </Box>
          {typeof data?.changePercent === 'number' ? (
            <CustomCard
              styles={{
                borderRadius: '50px',
                padding: '4px 8px',
                backgroundColor: data?.changePercent < 0 ? zeplinColor.Error : zeplinColor.Success,
                // width: '509px',
                height: '22px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography variant='label1' color={zeplinColor.Neutral1}>
                {`${data?.changePercent < 0 ? '' : '+'} ${data?.changePercent?.toFixed(2)}`}%
              </Typography>
            </CustomCard>
          ) : (
            <Skeleton
              variant='text'
              sx={{
                fontSize: theme.typography.body2,
                width: '100%',
                // Optionally adjust other styles here
              }}
            />
          )} 
        </Box>*/}
      </Box>
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            // justifyContent: 'space-between',
            // marginTop: '10%',
            alignItems: 'center',
            gap: '20px',
          }}
        >
          {/* <Skeleton variant='circular' width={40} height={40} /> */}
          <Skeleton variant='circular' width={200} height={200} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              // justifyContent: 'space-between',
              // marginTop: '10%',
              alignItems: 'center',
              gap: '20px',
            }}
          >
            <Skeleton
              variant='text'
              sx={{
                fontSize: theme.typography.body2,
                width: '8rem',
              }}
            />
            <Skeleton
              variant='text'
              sx={{
                fontSize: theme.typography.body2,
                width: '8rem',
              }}
            />
            <Skeleton
              variant='text'
              sx={{
                fontSize: theme.typography.body2,
                width: '8rem',
              }}
            />
          </Box>
        </Box>
      ) : data?.breakdown?.length > 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ position: 'relative' }}>
            {data?.breakdown?.length > 0 && (
              <PieChart
                sx={{ height: { xs: '12rem', xl: '15rem' }, position: 'relative' }}
                hideLegend={true}
                series={[
                  {
                    data: data?.breakdown?.map((item: any, index: any) => ({
                      id: index,
                      value: item.followers,
                      label: item.platform.charAt(0).toUpperCase() + item.platform.slice(1), // Capitalize platform name
                      color: platformColors[index], // Map color based on index
                    })),
                    innerRadius: isXs ? 80 : isXl ? 90 : 80,
                    // outerRadius: 100,
                    paddingAngle: 2,
                    cornerRadius: 3,

                    // startAngle: 0,
                    // endAngle: 0,

                    cx: 110,
                    // cy: 150,
                  },
                ]}
              />
            )}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '7rem',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                fontSize: { xs: '24px', xl: '28px' }, // Adjust as needed
                fontWeight: 700, // Adjust as needed
                color: 'rgba(0, 0, 0, 0.87)',
              }}
            >
              {formatNumber(data?.total)}
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              justifyContent: 'center',
            }}
          >
            {data?.breakdown?.map((val: any, ind: number) => (
              <Box
                sx={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                {/* <img src={'/assets/due-logo.png'} width={'20px'} height={'20px'} /> */}
                <Box
                  sx={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: platformColors[ind], // Use the color from the data
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    gap: '42px',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flex: 1,
                  }}
                >
                  <Typography variant='responsiveBody3' sx={{ flex: 1 }}>
                    {val?.platform.charAt(0).toUpperCase() + val?.platform.slice(1)}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '4px',
                      alignItems: 'center',
                      // justifyContent: 'space-between',
                      justifyContent: 'flex-end', // Align the second part to the right
                      flex: 1,
                    }}
                  >
                    <Typography variant='responsiveHeading4'>
                      {`${val?.percentage?.toFixed(2)}`}%
                    </Typography>
                    <Typography variant='responsiveHeading4' color={zeplinColor.Neutral8}>
                      ({formatNumber(val?.followers)})
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
            {/* <Box
            sx={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <img src={'/assets/due-logo.png'} width={'20px'} height={'20px'} />
            <Box
              sx={{
                display: 'flex',
                gap: '42px',
                alignItems: 'center',
                justifyContent: 'space-between',
                flex: 1,
              }}
            >
              <Typography variant='body3'>Due</Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: '4px',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flex: 1,
                }}
              >
                <Typography variant='heading4'>44.28%</Typography>
                <Typography variant='heading4' color={zeplinColor.Neutral8}>
                  (6.63M)
                </Typography>
              </Box>
            </Box>
          </Box> */}
            {/* <Box
            sx={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
              // justifyContent: 'space-between',
            }}
          >
            <img src={'/assets/overdue-icon.svg'} width={'20px'} height={'20px'} />
            <Box
              sx={{
                display: 'flex',
                gap: '24px',
                alignItems: 'center',
                justifyContent: 'space-between',
                flex: 1,
              }}
            >
              <Typography variant='body3'>Overdue</Typography>
              <Typography variant='heading4'>1</Typography>
            </Box>
          </Box> */}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            width: '42%',
            margin: 'auto',
            display: 'flex',
            backgroundColor: zeplinColor.Neutral1,
            flexDirection: 'column',
            gap: '24px',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <img src={'/assets/no-chart.svg'} width={'36px'} height={'36px'} alt='no-chart' />
          <Box
            sx={{
              display: 'flex',
              gap: '12px',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Typography variant='responsiveHeading3'>No Data Available</Typography>
            <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
              There’s no data to display right now. Data will appear here automatically once
              available.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FollowerChart;
